import * as React from 'react';
import {
  Input, Button, Icon, Form, FormField, FormItem, FormLabel, FormControl, FormMessage,
  ItalianAddressInput, ItalianAddressValueSchema
} from '@virlewood/board-79';
import { z } from 'zod';
import { motion } from 'framer-motion';
import { useOnboardingStore } from '../../stores/useOnboardingStore';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';


//TODO una volta fatta la migrazione a zod4 utilizzare lo schema esportato dalla libreria
const AddressInputSchema = z.discriminatedUnion('manual', [
  z.object({
    manual: z.literal(true),
    address: z.string().min(1, 'Indirizzo è obbligatorio'),
    city: z.string().min(1, 'Città è obbligatoria'),
    cap: z.string().min(1, 'CAP è obbligatorio'),
    province: z.string().min(1, 'Provincia è obbligatoria')
  }),
  z.object({
    manual: z.literal(false),
    placeId: z.string().min(1, 'Indirizzo selezionato è obbligatorio'),
    label: z.string(),
    sessionToken: z.string().uuid()
  })
]);
/**
 * Sales point schema - includes name and address
 * Used for additional sales points beyond the main one
 */
const salesPointSchema = z.object({
  name: z.string().min(3, 'Il nome deve essere di almeno 3 caratteri'),
  address: AddressInputSchema
});

export type SalesPoint = z.infer<typeof salesPointSchema>;

/**
 * Base info form schema - collects business name and addresses
 */
const baseInfoSchema = z.object({
  business_name: z.string().min(3, 'Il nome deve essere di almeno 3 caratteri'),
  mainSalesPointAddress: AddressInputSchema,
  extraSalesPoints: z.array(salesPointSchema)
});

type BaseInfoFormData = z.infer<typeof baseInfoSchema>;

/**
 * Helper function to create an empty sales point entry
 * Initializes with empty name and manual mode address with empty fields
 */
const createEmptySalesPoint = () => ({
  name: ''
} as SalesPoint);

const BaseInfo = () => {
  const { updateData, data, nextStep } = useOnboardingStore(state => state);

  const form = useForm<BaseInfoFormData>({
    resolver: zodResolver(baseInfoSchema),
    // Only validate on submit and blur, not on every change
    // This prevents "invalid" state from showing before user interacts
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: {
      business_name: data.business_name ?? '',
      mainSalesPointAddress: data.mainSalesPointAddress,
      extraSalesPoints: data.extraSalesPoints ?? []
    }
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'extraSalesPoints'
  });

  const onSubmit = (formData: BaseInfoFormData) => {
    // Save form data to store on submit
    updateData({
      business_name: formData.business_name,
      mainSalesPointAddress: formData.mainSalesPointAddress,
      extraSalesPoints: formData.extraSalesPoints,
      hasOneSalesPoint: formData.extraSalesPoints.length === 0
    });

    // Form validation passed, move to next step
    nextStep();
  };

  return (
    <motion.div
      className="w-full max-w-xl mx-auto"
    >
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          {/* Business Name Field */}
          <FormField
            control={form.control}
            name="business_name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Il nome della tua attività</FormLabel>
                <FormControl>
                  <Input {...field} autoFocus placeholder="es. Pizzeria da Piero" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Main Sales Point Address */}
          <FormField
            control={form.control}
            name="mainSalesPointAddress"
            render={({ field, fieldState }) => (
              <FormItem>
                <FormControl>
                  <ItalianAddressInput
                    className={'min-w-30'}
                    value={field.value}
                    onChange={field.onChange}
                    invalid={!!fieldState.error}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Extra Sales Points */}
          {fields.length > 0 && (
            <div className="space-y-4">
              <label className="block text-sm font-medium">
                Punti vendita aggiuntivi
              </label>

              {fields.map((field, index) => (
                <motion.div
                  key={field.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="border border-slate-200 rounded-xl p-4 space-y-4"
                >
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="font-medium text-sm">Punto vendita {index + 2}</h4>
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      onClick={() => remove(index)}
                    >
                      Rimuovi
                    </Button>
                  </div>

                  <FormField
                    control={form.control}
                    name={`extraSalesPoints.${index}.name`}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Nome</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="Nome del punto vendita" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name={`extraSalesPoints.${index}.address`}
                    render={({ field, fieldState }) => (
                      <FormItem>

                        <FormControl>
                          <ItalianAddressInput
                            value={field.value || undefined}
                            onChange={field.onChange}
                            invalid={!!fieldState.error}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </motion.div>
              ))}
            </div>
          )}

          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={() => append(createEmptySalesPoint())}
          >
            <Icon icon="fa6-solid:plus" className="mr-2" />
            Aggiungi un altro punto vendita
          </Button>

          {/* Submit Button */}
          <Button
            type="submit"
            variant="primary"
            size="lg"
            className={'w-full max-w-xs mx-auto mt-8'}
          >
            Continua
          </Button>
        </form>
      </Form>
    </motion.div>
  );
};

export default BaseInfo;
